/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/core components
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles from '@material-ui/core/styles/withStyles';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { adCurrencies } from 'config';
// our components
import { CURRENCY_FILTER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';
import TextField from '@material-ui/core/TextField';

/**
 * @description
 * Validation form's currency filter.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    currency: Yup.string().required(t('errors.required')),
    rate: Yup.number().required(t('errors.required')),
  });

/**
 * @class
 * @extends React.Component
 */
class CurrencyFilterForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { id, rate, currency } = props;

    this.state = {
      fields: {
        id,
        rate,
        currency,
      },
    };

    this.formikRef = null;
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(CURRENCY_FILTER.LIST);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Render Ad form.
   * @returns {*} - No returns value.
   */
  render() {
    const {
      classes,
      errors: serverErrors,
      editMode,
      submitLoading,
      currencyFilterSave,
      closeNotification,
      t,
    } = this.props;
    const { fields } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);
    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          close
          closeNotification={closeNotification}
        />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={fields}
          validationSchema={validationSchema}
          onSubmit={values =>
            currencyFilterSave({
              ...values,
            })
          }
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <GridItem className={classes.cardContainer}>
              <div
                className={`${classes.row} ${classes.contentBtns}`}
                style={{ justifyContent: 'space-between', display: 'flex' }}
              >
                <h3>
                  {editMode
                    ? t('currency_filter.form.edit')
                    : t('currency_filter.form.create')}
                </h3>
              </div>
              <form onSubmit={handleSubmit}>
                <Card>
                  <Loading show={submitLoading} />
                  <CardBody>
                    <GridContainer direction="column" alignItems="center">
                      <GridItem xs={12} sm={10}>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <InputLabel
                            htmlFor="name"
                            className={classes.selectLabel}
                          >
                            {t('currency_filter.form.fields.currency')}
                          </InputLabel>
                          <Select
                            style={{ marginLeft: '20px' }}
                            value={values.currency}
                            onChange={e => handleChange(e)}
                            MenuProps={{ className: classes.selectMenu }}
                            inputProps={{
                              name: 'currency',
                              id: 'currency',
                            }}
                          >
                            {Object.keys(adCurrencies).map(currency => (
                              <MenuItem
                                key={adCurrencies[currency]}
                                classes={{
                                  root: classes.selectMenuItem,
                                  selected: classes.selectMenuItemSelected,
                                }}
                                value={adCurrencies[currency]}
                              >
                                {adCurrencies[currency]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <TextField
                            id="rate"
                            label={t('currency_filter.form.fields.rate')}
                            margin="normal"
                            helperText={touched.rate ? errors.rate : ''}
                            error={touched.rate && Boolean(errors.rate)}
                            InputProps={{
                              value: values.rate,
                              onChange: e => handleChange(e),
                              onBlur: handleBlur,
                              type: 'number',
                            }}
                          />
                        </FormControl>
                        <div className={classes.section}>
                          <GridContainer direction="row-reverse">
                            <GridItem xs={12} sm={6}>
                              <div className={classes.contentBtns}>
                                <Button
                                  id="save-currency-filter"
                                  color="primary"
                                  type="submit"
                                  style={{ float: 'right' }}
                                >
                                  {editMode
                                    ? t('currency_filter.form.edit_btn')
                                    : t('currency_filter.form.create_btn')}
                                </Button>
                              </div>
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              <Button
                                id="cancel-create-currency-filter"
                                simple
                                color="danger"
                                onClick={this.handleCancelBtn}
                                style={{ float: 'left' }}
                              >
                                {t('common.cancel')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </div>
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              </form>
            </GridItem>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * CurrencyFilterForm PropTypes
 * @param classes - styles received from withStyles
 * @param rate - Rate
 * @param currency - Currency
 * @param errors - Errors data list
 * @param editMode - To active editing mode
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @callback currencyFilterSave - callback called when user clicks save button
 * @callback closeNotification - callback to close notification
 */

CurrencyFilterForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  id: PropTypes.string,
  rate: PropTypes.number,
  currency: PropTypes.string,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  currencyFilterSave: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  closeNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

CurrencyFilterForm.defaultProps = {
  editMode: false,
  id: undefined,
  rate: 1,
  currency: adCurrencies.CUP,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const CurrencyFilterFormWrapper = props => <CurrencyFilterForm {...props} />;

export default withTranslation()(
  withStyles(validationFormsStyle)(CurrencyFilterFormWrapper),
);
